import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/auth.service';
import pushService from '../services/push.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);

    // Silently refresh user profile to pick up role changes (e.g. admin approved partner)
    if (currentUser) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://cvmcdnkfrfhhlzwlekab.supabase.co';
        const API_URL = process.env.REACT_APP_API_URL || `${SUPABASE_URL}/functions/v1`;
        fetch(`${API_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.user) {
              setUser(data.user);
              const s = localStorage.getItem('token') ? localStorage : sessionStorage;
              s.setItem('user', JSON.stringify(data.user));
            }
          })
          .catch(() => {});
        // Subscribe to push notifications silently
        pushService.subscribe().catch(() => {});
      }
    }
  }, []);

  const login = async (email, password, recaptchaToken) => {
    const data = await authService.login(email, password, recaptchaToken);
    if (data.token) {
      setUser(data.user);
      pushService.subscribe().catch(() => {});
    }
    return data;
  };

  const send2FACode = async (twoFactorSession, method, recaptchaToken = null) => {
    return authService.send2FACode(twoFactorSession, method, recaptchaToken);
  };

  const verify2FACode = async (twoFactorSession, code, rememberDevice = false) => {
    const data = await authService.verify2FACode(twoFactorSession, code, rememberDevice);
    if (data.token) {
      setUser(data.user);
      pushService.subscribe().catch(() => {});
    }
    return data;
  };

  const register = async (userData) => {
    const data = await authService.register(userData);
    if (data.token) setUser(data.user);
    return data;
  };

  const verifyEmail = async (verificationToken, code) => {
    const data = await authService.verifyEmail(verificationToken, code);
    if (data.token) setUser(data.user);
    return data;
  };

  const resendVerificationEmail = async (verificationToken) => {
    return authService.resendVerificationEmail(verificationToken);
  };

  const googleLogin = async (params) => {
    const data = await authService.googleLogin(
      typeof params === 'string' ? { credential: params } : params
    );
    if (data.token) {
      setUser(data.user);
      pushService.subscribe().catch(() => {});
    }
    return data;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    // Write to whichever storage holds the current session
    const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
    storage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    loading,
    login,
    register,
    googleLogin,
    send2FACode,
    verify2FACode,
    verifyEmail,
    resendVerificationEmail,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
